import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import { AccountBalance, Delete, VpnKey } from "@material-ui/icons"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "../../../utils/notificacoes"
import { DadosBancariosSubcontaActionsTypes } from "../context/dadosBancariosSubcontaActions"
import { useDadosBancariosSubcontaContext } from "../context/useDadosBancariosSubcontaContext"
import { excluirDadosBancarios, salvarCredenciaisIugu } from "../../../services/dadosBancariosSubconta"
import useAppStore from "../../../stores/AppStore"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderAdm: {
        backgroundColor: "#fff3f3",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #ffcdd2",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardHeaderTitleAdm: {
        fontWeight: 600,
        color: "#c62828",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    iconHeaderAdm: {
        color: "#c62828",
        marginRight: theme.spacing(1),
    },
    fieldGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    fieldFull: {
        gridColumn: "1 / -1",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76,175,80,0.4)",
        "&:hover": { backgroundColor: "#388e3c" },
    },
    deleteButton: {
        borderRadius: 25,
        backgroundColor: "#e53935",
        color: "white",
        padding: theme.spacing(1, 3),
        fontWeight: 600,
        "&:hover": { backgroundColor: "#b71c1c" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    legend: {
        color: "#7f8c8d",
        fontSize: "0.85rem",
        marginTop: theme.spacing(0.5),
    },
    admTag: {
        fontSize: "0.75rem",
        color: "#c62828",
        fontWeight: 600,
        backgroundColor: "#ffcdd2",
        borderRadius: 8,
        padding: "2px 8px",
        marginLeft: theme.spacing(1),
    },
}))

export function FormularioDadosBancarios() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento, salvar, criar } = useDadosBancariosSubcontaContext()
    const { enqueueSnackbar } = useSnackbar()
    const configs = useAppStore(s => s.configuracoes)
    const isClienteFiel = configs?.user?.perfil === "CLIENTEFIEL"

    const [nomeSubconta, setNomeSubconta] = useState("")
    const [tokenIuguId, setTokenIuguId] = useState("")
    const [tokenIuguCabecalho, setTokenIuguCabecalho] = useState("")

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    useEffect(() => {
        setNomeSubconta(dados.nomeEstabelecimento)
    }, [dados.nomeEstabelecimento])

    const campo = (key: keyof typeof dados) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { value: unknown }>) => {
            dispatch({
                type: DadosBancariosSubcontaActionsTypes.ATUALIZAR_CAMPO,
                payload: { campo: key, valor: e.target.value },
            })
        }

    const bancoSelecionado = dados.bancos.find(b => b.humanName === dados.nomeBanco)
    const bancoTemOperacao = bancoSelecionado?.hasOperation ?? false

    const handleCriar = async () => {
        if (!nomeSubconta.trim()) { alertStart("Preencha o nome do estabelecimento.", "error"); return }
        try {
            await criar(nomeSubconta)
            alertStart("Subconta criada com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao criar subconta.", "error")
        }
    }

    const handleSalvar = async () => {
        if (!dados.responsavelName.trim()) { alertStart("Preencha o campo Responsável.", "error"); return }
        if (!dados.responsavelCpf.trim()) { alertStart("Preencha o campo CPF do Responsável.", "error"); return }
        if (dados.dataPersonType === null) { alertStart("Preencha o campo Tipo de Pessoa.", "error"); return }
        if (!dados.cpfCnpj.trim()) { alertStart("Preencha o campo CPF/CNPJ.", "error"); return }
        if (!dados.nomeBanco) { alertStart("Selecione o Banco.", "error"); return }
        if (!dados.tipoConta) { alertStart("Selecione o Tipo de Conta.", "error"); return }
        if (bancoTemOperacao && !dados.operacao.trim()) { alertStart("Preencha o campo Operação.", "error"); return }
        if (!dados.agencia.trim()) { alertStart("Preencha o campo Agência.", "error"); return }
        if (!dados.numeroConta.trim()) { alertStart("Preencha o número da conta.", "error"); return }
        try {
            await salvar()
            alertStart("Dados bancários salvos com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar dados bancários.", "error")
        }
    }

    const handleSalvarCredenciais = async () => {
        if (!tokenIuguId.trim()) { alertStart("Preencha o campo ID Conta.", "error"); return }
        if (!tokenIuguCabecalho.trim()) { alertStart("Preencha o campo Token.", "error"); return }
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            const response = await salvarCredenciaisIugu(autorizacao, tokenIuguId, tokenIuguCabecalho)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao salvar credenciais.", "error")
                return
            }
            dispatch({
                type: DadosBancariosSubcontaActionsTypes.ATUALIZAR_CAMPO,
                payload: { campo: "possuiTokensCriadosIugu", valor: response.possuiTokensCriadosIugu },
            })
            alertStart("Credenciais salvas com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar credenciais.", "error")
        }
    }

    const handleExcluir = async () => {
        if (!window.confirm("Atenção! Tem certeza que deseja remover esta conta bancária?")) return
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            const response = await excluirDadosBancarios(autorizacao)
            if (response && "retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao excluir.", "error")
                return
            }
            alertStart("Conta excluída com sucesso.", "success")
            dispatch({ type: DadosBancariosSubcontaActionsTypes.CARREGAR, payload: { ...dados, possuiTokensCriadosIugu: false, possuiDadosBancariosCompletos: false, id: null } })
        } catch (e) {
            alertStart((e as Error).message || "Erro ao excluir.", "error")
        }
    }

    const disabled = dados.possuiDadosBancariosCompletos

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant="h5" className={classes.titleText}>
                        Dados Bancários
                    </Typography>
                    <Typography className={classes.legend}>
                        Cadastre e gerencie seus dados bancários para recebimento via Iugu.
                    </Typography>
                </Box>

                {/* Seção: Criação de Contas */}
                {!dados.possuiTokensCriadosIugu && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <AccountBalance className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Criação de Conta
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Box className={classes.fieldGrid}>
                                <Box className={classes.fieldFull}>
                                    <TextField
                                        label="Nome do Estabelecimento"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={nomeSubconta}
                                        onChange={e => setNomeSubconta(e.target.value)}
                                    />
                                </Box>
                                <Box className={classes.fieldFull} display="flex" justifyContent="flex-end">
                                    <Button className={classes.saveButton} onClick={handleCriar}>
                                        Criar Subconta
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Formulário completo após tokens criados */}
                {dados.possuiTokensCriadosIugu && (
                    <>
                        {/* Informações do Estabelecimento */}
                        {!dados.possuiDadosBancariosCompletos && (
                            <Paper className={classes.configCard}>
                                <Box className={classes.cardHeader}>
                                    <AccountBalance className={classes.iconHeader} />
                                    <Typography variant="h6" className={classes.cardHeaderTitle}>
                                        Informações do Estabelecimento
                                    </Typography>
                                </Box>
                                <Box className={classes.cardContent}>
                                    <Box className={classes.fieldGrid}>
                                        <TextField
                                            label="Nome do Estabelecimento"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.nomeEmpresa}
                                            onChange={campo("nomeEmpresa")}
                                            disabled={disabled}
                                        />
                                        <TextField
                                            label="Telefone"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.telefone}
                                            onChange={campo("telefone")}
                                            disabled={disabled}
                                        />
                                        <TextField
                                            label="Estado"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.estado}
                                            onChange={campo("estado")}
                                            disabled={disabled}
                                        />
                                        <TextField
                                            label="CEP"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.cep}
                                            onChange={campo("cep")}
                                            disabled={disabled}
                                        />
                                        <TextField
                                            label="Cidade"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.cidade}
                                            onChange={campo("cidade")}
                                            disabled={disabled}
                                        />
                                        <Box className={classes.fieldFull}>
                                            <TextField
                                                label="Endereço"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                value={dados.endereco}
                                                onChange={campo("endereco")}
                                                disabled={disabled}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {/* Dados Bancários */}
                        <Paper className={classes.configCard}>
                            <Box className={classes.cardHeader}>
                                <AccountBalance className={classes.iconHeader} />
                                <Typography variant="h6" className={classes.cardHeaderTitle}>
                                    Dados Bancários
                                </Typography>
                            </Box>
                            <Box className={classes.cardContent}>
                                <Box className={classes.fieldGrid}>
                                    <TextField
                                        label="Responsável"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.responsavelName}
                                        onChange={campo("responsavelName")}
                                        disabled={disabled}
                                    />
                                    <TextField
                                        label="CPF do Responsável"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.responsavelCpf}
                                        onChange={campo("responsavelCpf")}
                                        disabled={disabled}
                                    />
                                    <TextField
                                        select
                                        label="Tipo de Pessoa"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.dataPersonType ?? ""}
                                        onChange={e =>
                                            dispatch({
                                                type: DadosBancariosSubcontaActionsTypes.ATUALIZAR_CAMPO,
                                                payload: {
                                                    campo: "dataPersonType",
                                                    valor: e.target.value !== "" ? Number(e.target.value) : null,
                                                },
                                            })
                                        }
                                        disabled={disabled}
                                    >
                                        <MenuItem value="">- Escolha um Tipo -</MenuItem>
                                        <MenuItem value={0}>Pessoa Jurídica</MenuItem>
                                        <MenuItem value={1}>Pessoa Física</MenuItem>
                                    </TextField>
                                    <TextField
                                        label={dados.dataPersonType === 0 ? "CNPJ" : "CPF"}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.cpfCnpj}
                                        onChange={campo("cpfCnpj")}
                                        disabled={disabled}
                                    />
                                    <TextField
                                        select
                                        label="Banco"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.nomeBanco}
                                        onChange={campo("nomeBanco")}
                                        disabled={disabled}
                                    >
                                        <MenuItem value="">- Escolha o Banco -</MenuItem>
                                        {dados.bancos.map(b => (
                                            <MenuItem key={b.humanName} value={b.humanName}>
                                                {b.humanName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        label="Tipo de Conta"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.tipoConta}
                                        onChange={campo("tipoConta")}
                                        disabled={disabled}
                                    >
                                        <MenuItem value="">- Escolha o tipo da Conta -</MenuItem>
                                        <MenuItem value="Corrente">Corrente</MenuItem>
                                        <MenuItem value="Poupança">Poupança</MenuItem>
                                    </TextField>
                                    {bancoTemOperacao && (
                                        <TextField
                                            label="Operação (Caixa Econômica)"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={dados.operacao}
                                            onChange={campo("operacao")}
                                            disabled={disabled}
                                        />
                                    )}
                                    <TextField
                                        label="Agência"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.agencia}
                                        onChange={campo("agencia")}
                                        disabled={disabled}
                                        helperText="Bradesco e Banco do Brasil: incluir dígito da agência"
                                    />
                                    <TextField
                                        label="Número da Conta (com dígito)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={dados.numeroConta}
                                        onChange={campo("numeroConta")}
                                        disabled={disabled}
                                    />
                                </Box>

                                {!disabled && (
                                    <Box display="flex" justifyContent="flex-end" mt={3}>
                                        <Button className={classes.saveButton} onClick={handleSalvar}>
                                            Salvar Dados Bancários
                                        </Button>
                                    </Box>
                                )}

                                {disabled && (
                                    <Box mt={2}>
                                        <Typography className={classes.legend}>
                                            Dados bancários verificados — entre em contato com o suporte para alterações.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        {/* Seção ADM: Excluir (visível apenas quando verificada) */}
                        {isClienteFiel && dados.possuiDadosBancariosCompletos && (
                            <Paper className={classes.configCard}>
                                <Box className={classes.cardHeaderAdm}>
                                    <Box display="flex" alignItems="center">
                                        <Delete className={classes.iconHeaderAdm} />
                                        <Typography variant="h6" className={classes.cardHeaderTitleAdm}>
                                            Informações Úteis
                                        </Typography>
                                        <span className={classes.admTag}>ADM</span>
                                    </Box>
                                    <Button
                                        className={classes.deleteButton}
                                        startIcon={<Delete />}
                                        onClick={handleExcluir}
                                    >
                                        Excluir Dados Bancários
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                    </>
                )}

                {/* Seção ADM: Credenciais Iugu */}
                {isClienteFiel && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeaderAdm}>
                            <Box display="flex" alignItems="center">
                                <VpnKey className={classes.iconHeaderAdm} />
                                <Typography variant="h6" className={classes.cardHeaderTitleAdm}>
                                    Configuração das Credenciais
                                </Typography>
                                <span className={classes.admTag}>ADM</span>
                            </Box>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Box className={classes.fieldGrid}>
                                <TextField
                                    label="ID Conta"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={tokenIuguId}
                                    onChange={e => setTokenIuguId(e.target.value)}
                                />
                                <TextField
                                    label="Token"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="password"
                                    value={tokenIuguCabecalho}
                                    onChange={e => setTokenIuguCabecalho(e.target.value)}
                                />
                                <Box className={classes.fieldFull} display="flex" justifyContent="flex-end">
                                    <Button className={classes.saveButton} startIcon={<VpnKey />} onClick={handleSalvarCredenciais}>
                                        Salvar Credenciais
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
